/* Reusable full-screen dialog wrapper for feature explanation screens.
   Provides consistent scroll, close button, and entrance animation. */

import type { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface FeatureInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  className?: string;
}

export function FeatureInfoDialog({
  open,
  onOpenChange,
  title,
  children,
  className,
}: FeatureInfoDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-lg max-h-[90vh] p-0 gap-0 overflow-hidden",
          className,
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          Learn how {title} works in FeedKiwi
        </DialogDescription>
        <ScrollArea className="max-h-[90vh]">
          <div className="p-5 sm:p-6">{children}</div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

/* Reusable section card for consistent styling across info screens. */
export function InfoSection({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border/60 bg-muted/30 p-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

/* Section title with icon badge. */
export function InfoSectionTitle({
  icon,
  iconColor = "text-primary",
  iconBg = "bg-primary/10",
  children,
}: {
  icon: ReactNode;
  iconColor?: string;
  iconBg?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <div
        className={cn(
          "flex items-center justify-center size-8 rounded-lg shrink-0",
          iconBg,
          iconColor,
        )}
      >
        {icon}
      </div>
      <h3 className="text-sm font-semibold">{children}</h3>
    </div>
  );
}

/* Numbered step row for flow explanations. */
export function InfoStep({
  number,
  icon,
  color,
  title,
  description,
}: {
  number: number;
  icon: ReactNode;
  color: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="flex items-center justify-center size-9 rounded-xl shrink-0 relative"
        style={{ backgroundColor: `color-mix(in oklch, ${color} 15%, transparent)` }}
      >
        <span style={{ color }} className="[&>svg]:size-4">
          {icon}
        </span>
        <span
          className="absolute -top-1 -right-1 size-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
          style={{ backgroundColor: color }}
        >
          {number}
        </span>
      </div>
      <div className="pt-0.5 min-w-0">
        <p className="text-sm font-semibold leading-tight">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

export function InfoHeroBadge({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-primary/70",
        className,
      )}
    >
      {children}
    </span>
  );
}
